import {
	useCallback, useEffect, useMemo, useState
} from 'react';
import { connect } from 'react-redux';
import { Chip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import {
	CheckCircle2,
	Send,
	BookmarkCheck,
} from 'lucide-react';

// import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CircularProgress from '@mui/material/CircularProgress';
import SwapVertIcon from '@mui/icons-material/SwapVert';
// import WebIcon from '@mui/icons-material/Web';
import CachedIcon from '@mui/icons-material/Cached';
import { modalActions } from '../../../../Actions';
import { modalTypes, urls } from '../../../../Constants';
import {
	convertWeightUnit,
	usePagination,
} from '../../../../Helpers';
// eslint-disable-next-line import/no-cycle
import { ImageThumbnail } from '../../../Image';
import styles from './ProductImagesResult.module.scss';
import loadMoreLoader from '../../../../Images/loadmoreLoader.svg';
import { annotationServices, cvatServices } from '../../../../Services';
import { notificationServices } from '../../../../Services/Notification';
import { legendWithColors } from '../../../../Pages/Analytics/ProductAnalysis/ProductImagesPage/ProductImagesPage';

const chipStyle = {
	color: 'white', // Set text color to white
	border: '1px solid white', // Set border color to white
};

const cvatStatusConfig = {
	eligible: {
		label: 'CVAT eligible',
		Icon: CheckCircle2,
		className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
	},
	selected: {
		label: 'Queued for CVAT',
		Icon: BookmarkCheck,
		className: 'border-yellow-200 bg-yellow-50 text-yellow-800',
	},
	sent: {
		label: 'Sent to CVAT',
		Icon: Send,
		className: 'border-blue-200 bg-blue-50 text-blue-700',
	},
};

const getCvatStatus = (annotation) => {
	const status = (
		annotation?.CvatAnnotationStatus ??
		annotation?.cvatAnnotationStatus ??
		annotation?.cvat_annotation_status
	);
	return typeof status === 'string' ? status.toLowerCase() : status;
};

const getShapeModelId = (product, annotation = null) => {
	return (
		product?.shapeModelId ??
		product?.ShapeModelId ??
		product?.shape_model_id ??
		annotation?.shapeModelId ??
		annotation?.ShapeModelId ??
		annotation?.shape_model_id ??
		annotation?.CvatAnnotationShapeModelId ??
		annotation?.cvatAnnotationShapeModelId ??
		annotation?.cvat_annotation_shape_model_id
	);
};

const isCvatSelectable = (annotation) => {
	const status = getCvatStatus(annotation);
	return ['eligible', 'selected'].includes(status);
};

const uniqueValues = (values) => Array.from(new Set(values));

const responseHasKey = (data, keys) => {
	return keys.some((key) => {
		return Object.prototype.hasOwnProperty.call(data, key);
	});
};

const getResponseIds = (response, keys, fallbackIds = []) => {
	const data = response?.data ?? response ?? {};
	if (!responseHasKey(data, keys)) {
		return fallbackIds;
	}
	const ids = keys.reduce((acc, key) => {
		if (acc.length > 0) return acc;
		return Array.isArray(data?.[key]) ? data[key] : [];
	}, []);
	return uniqueValues(ids.filter(Boolean));
};

const addStatusOverrides = (overrides, ids, status) => {
	return ids.reduce((next, id) => {
		return {
			...next,
			[id]: status,
		};
	}, overrides);
};

const buildCvatSelectionMessage = ({
	selectedQcIds,
	removedQcIds,
	alreadySelectedQcIds,
	alreadySentQcIds,
	missingQcIds,
}) => {
	const changedCount = selectedQcIds.length + removedQcIds.length;
	if (changedCount > 0) {
		const parts = [];
		if (selectedQcIds.length > 0) {
			parts.push(`${selectedQcIds.length} queued`);
		}
		if (removedQcIds.length > 0) {
			parts.push(`${removedQcIds.length} removed`);
		}
		return {
			type: 'success',
			message: `CVAT selection updated: ${parts.join(', ')}`,
		};
	}

	const skippedCount =
		alreadySelectedQcIds.length +
		alreadySentQcIds.length +
		missingQcIds.length;
	if (skippedCount > 0) {
		const parts = [];
		if (alreadySelectedQcIds.length > 0) {
			parts.push(`${alreadySelectedQcIds.length} already selected`);
		}
		if (alreadySentQcIds.length > 0) {
			parts.push(`${alreadySentQcIds.length} already sent`);
		}
		if (missingQcIds.length > 0) {
			parts.push(`${missingQcIds.length} missing`);
		}
		return {
			type: 'warning',
			message: `No CVAT selection changed: ${parts.join(', ')}`,
		};
	}

	return {
		type: 'warning',
		message: 'No CVAT selection changed',
	};
};

const ProductImageResult = (props) => {
	const { page, limit, nextPage } = usePagination({
		customLimit: 6,
	});
	const history = useHistory();

	const [disabled, setDisabled] = useState(false);
	const [checkedItemList, setCheckedItemList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [cvatSelectionLoading, setCvatSelectionLoading] = useState(false);
	const [cvatStatusOverrides, setCvatStatusOverrides] = useState({});

	const {
		isInternal,
		product,
		fetchQCData,
		egaPercentageSelected,
		setRefreshLoading,
		toggleRetagModal,
		refreshLoading,
		toggleModal,
		isUS,
		fetchProductImagesById,
		identification,
		machineIds,
	} = props;
	const [annotations, setAnnotations] = useState(product?.Annotation ?? []);

	useEffect(() => {
		setAnnotations(
			(product?.Annotation ?? []).map((ann) => {
				if (cvatStatusOverrides[ann.qcId]) {
					return {
						...ann,
						CvatAnnotationStatus: cvatStatusOverrides[ann.qcId],
					};
				}
				return ann;
			})
		);
	}, [product?.Annotation, product?.id, cvatStatusOverrides]);

	const loadMoreImages = async () => {
		try {
			setDisabled(true);
			setLoading(true);
			await fetchProductImagesById({
				productId: product.id,
				page: page + 1,
				machineIds,
				identification,
				limit,
			});
			nextPage();
		} catch (err) {
			//
		} finally {
			setDisabled(false);
			setLoading(false);
		}
	};
	const refresh = useCallback(
		async (addLimit) => {
			try {
				await fetchProductImagesById({
					productId: product.id,
					page: 1,
					identification,
					typeRefresh: true,
					limit: annotations.length + (addLimit ?? 0),
				});
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err);
			} finally {
				setRefreshLoading(false);
			}
		},
		[fetchProductImagesById, product, annotations.length]
	);

	const openModal = (item) => {
		toggleModal(true, {
			item,
		});
	};

	const handleQcPageRoute = useCallback(() => {
		const qcIdsString = checkedItemList
			?.map((ele) => {
				return ele?.qcId;
			})
			.join(',');
		const path = `${
			urls.EXTENSION + urls.QC_HISTORY_LIST
		}?s_no_filter=${qcIdsString}`;

		window.open(path);
		// history.push({
		// pathname: path,
		// });
	}, [checkedItemList, history]);

	const validateCvatSelection = (list, silent = false) => {
		const cvatItems = list.filter(isCvatSelectable);
		if (cvatItems.length === 0) return true;

		const statuses = new Set(cvatItems.map(getCvatStatus));
		if (statuses.size > 1) {
			if (!silent) {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Cannot select both Eligible and Selected CVAT images at the same time.',
				});
			}
			return false;
		}
		return true;
	};

	const updateChecked = useCallback(
		(item) => {
			setCheckedItemList((prev) => {
				const list = [...prev];
				const index = prev.findIndex((ele) => {
					return ele?.qcId === item?.qcId;
				});
				if (index !== -1) {
					list.splice(index, 1);
				} else {
					list.push(item);
					if (!validateCvatSelection(list)) {
						return prev;
					}
				}
				return list;
			});
		},
		[setCheckedItemList]
	);
	const inverseSelection = useCallback(() => {
		setCheckedItemList((prev) => {
			const newArray = [];
			annotations?.forEach((ele) => {
				if (
					prev.findIndex((annotation) => {
						return annotation?.qcId === ele?.qcId;
					}) === -1
				) {
					newArray.push(ele);
				}
			});
			if (!validateCvatSelection(newArray)) {
				return prev;
			}
			return newArray;
		});
	}, [setCheckedItemList, annotations]);

	useEffect(() => {
		if (refreshLoading?.labelId === product?.id) {
			refresh(refreshLoading?.addLimit);
		}
	}, [refreshLoading?.labelId]);

	const validate = useCallback(
		async (aiCorrect, labelId = '') => {
			setRefreshLoading({
				labelId: product?.id,
			});
			const qcIdList = checkedItemList.map((ele) => {
				return ele.qcId;
			});
			const data = {
				aiCorrect,
				labelId,
				qcIdList,
			};
			try {
				const resp = await annotationServices.bulkValidation({
					data,
				});
				notificationServices.generateNotification({
					type: 'success',
					message: resp?.message,
				});
				setCheckedItemList([]);
			} catch (err) {
				notificationServices.generateNotification({
					type: 'error',
					message: err,
				});
				// eslint-disable-next-line no-console
				console.log(err);
			}
		},
		[checkedItemList, setCheckedItemList]
	);

	// eslint-disable-next-line no-unused-vars
	const revalidateSKU = () => {
		toggleRetagModal(true, {
			functionToExecute: (labelId) => {
				validate(false, labelId);
				setTimeout(() => {
					setRefreshLoading({
						labelId,
						addLimit: checkedItemList.length,
					});
				}, 200);
			},
		});
	};

	const selectDeselect = useCallback(() => {
		setCheckedItemList((prev) => {
			if (prev.length === 0) {
				if (!validateCvatSelection(annotations)) {
					return prev;
				}
				return annotations;
			}
			return [];
		});
	}, [setCheckedItemList, annotations]);

	const selectedCvatItems = useMemo(() => {
		return checkedItemList.filter((annotation) => {
			return isCvatSelectable(annotation);
		});
	}, [checkedItemList]);

	const cvatSelectionActionLabel = useMemo(() => {
		const eligibleCount = selectedCvatItems.filter((annotation) => {
			return getCvatStatus(annotation) === 'eligible';
		}).length;
		const selectedCount = selectedCvatItems.filter((annotation) => {
			return getCvatStatus(annotation) === 'selected';
		}).length;

		if (eligibleCount > 0 && selectedCount === 0) {
			return `Queue for CVAT (${eligibleCount})`;
		}
		if (selectedCount > 0 && eligibleCount === 0) {
			return `Remove from CVAT Queue (${selectedCount})`;
		}
		return `Update CVAT Queue (${selectedCvatItems.length})`;
	}, [selectedCvatItems]);

	const applyCvatSelection = useCallback(async () => {
		const shapeModelId = getShapeModelId(product, selectedCvatItems?.[0]);
		if (!shapeModelId) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No shape model id was found for the selected images',
			});
			return;
		}

		const queueQcIds = uniqueValues(
			selectedCvatItems
				.filter((annotation) => getCvatStatus(annotation) === 'eligible')
				.map((annotation) => annotation?.qcId)
				.filter(Boolean)
		);
		const unqueueQcIds = uniqueValues(
			selectedCvatItems
				.filter((annotation) => getCvatStatus(annotation) === 'selected')
				.map((annotation) => annotation?.qcId)
				.filter(Boolean)
		);

		if (queueQcIds.length === 0 && unqueueQcIds.length === 0) {
			return;
		}

		try {
			setCvatSelectionLoading(true);
			const [queueResponse, unqueueResponse] = await Promise.all([
				queueQcIds.length > 0
					? cvatServices.queueSelection({
						data: {
							shapeModelId,
							qcIds: queueQcIds,
						},
					})
					: null,
				unqueueQcIds.length > 0
					? cvatServices.unqueueSelection({
						data: {
							shapeModelId,
							qcIds: unqueueQcIds,
						},
					})
					: null,
			]);
			const selectedQcIds = getResponseIds(
				queueResponse,
				['selectedQcIds', 'selected_qc_ids'],
				queueQcIds
			);
			const alreadySelectedQcIds = getResponseIds(
				queueResponse,
				['alreadySelectedQcIds', 'already_selected_qc_ids']
			);
			const removedQcIds = getResponseIds(
				unqueueResponse,
				['removedQcIds', 'removed_qc_ids'],
				unqueueQcIds
			);
			const alreadySentQcIds = uniqueValues([
				...getResponseIds(
					queueResponse,
					['alreadySentQcIds', 'already_sent_qc_ids']
				),
				...getResponseIds(
					unqueueResponse,
					['alreadySentQcIds', 'already_sent_qc_ids']
				),
			]);
			const missingQcIds = uniqueValues([
				...getResponseIds(
					queueResponse,
					['missingQcIds', 'missing_qc_ids']
				),
				...getResponseIds(
					unqueueResponse,
					['missingQcIds', 'missing_qc_ids']
				),
			]);
			setCvatStatusOverrides((prev) => {
				let next = {
					...prev,
				};
				next = addStatusOverrides(next, selectedQcIds, 'selected');
				next = addStatusOverrides(next, alreadySelectedQcIds, 'selected');
				next = addStatusOverrides(next, removedQcIds, 'eligible');
				next = addStatusOverrides(next, alreadySentQcIds, 'sent');
				return next;
			});
			setCheckedItemList([]);
			await refresh(0);
			const notification = buildCvatSelectionMessage({
				selectedQcIds,
				removedQcIds,
				alreadySelectedQcIds,
				alreadySentQcIds,
				missingQcIds,
			});
			notificationServices.generateNotification({
				type: notification.type,
				message: notification.message,
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setCvatSelectionLoading(false);
		}
	}, [product, refresh, selectedCvatItems]);
	// const MarkAiCorrect = useCallback((item) => {}, [checkedItemList]);
	// const MarkAiInCorrect = useCallback(() => {}, [setCheckedItemList]);

	return (
		<div className={styles.result}>
			<p className={styles['result-header']}>
				<div className={styles?.text}>
					{product.label}
					<span className={styles.count}>
						{` (Showing ${annotations.length} of ${
							product?.count ?? 0
						} image(s))`}
					</span>
				</div>
				{isInternal && (
					<div className={styles?.selectionButtons}>
						{/* <Chip
							style={chipStyle}
							size='small'
							label='refresh'
							onClick={refresh}
						/> */}
						<Chip
							style={{
								...chipStyle,
								border: 'none',
							}}
							size='small'
							onClick={() => {
								refresh();
							}}
							icon={(
								<CachedIcon
									style={{
										color: 'white',
									}}
								/>
							)}
						/>
						{checkedItemList?.length > 0 && (
							<>
								<Chip
									style={chipStyle}
									size='small'
									onClick={() => {
										validate(true);
									}}
									label='Mark Ai-Correct'
									icon={(
										<DoneAllIcon
											style={{
												color: 'white',
											}}
										/>
									)}
								/>

								<Chip
									style={chipStyle}
									size='small'
									label='Mark Ai-Incorrect'
									onClick={revalidateSKU}
									icon={(
										<CloseIcon
											style={{
												color: 'white',
											}}
										/>
									)}
								/>
								{selectedCvatItems?.length > 0 && (
									<Chip
										style={chipStyle}
										size='small'
										label={cvatSelectionActionLabel}
										disabled={cvatSelectionLoading}
										onClick={applyCvatSelection}
									/>
								)}
							</>
						)}

						<Chip
							style={chipStyle}
							size='small'
							label='Invert Selection'
							onClick={inverseSelection}
							icon={(
								<SwapVertIcon
									style={{
										color: 'white',
									}}
								/>
							)}
						/>

						{checkedItemList?.length > 0 && (
							<>
								<Chip
									onClick={selectDeselect}
									style={chipStyle}
									size='small'
									label={
										checkedItemList?.length === 0
											? 'Select All'
											: 'Deselect All'
									}
								/>
								<Chip
									style={chipStyle}
									icon={(
										<CallMadeIcon
											style={{
												color: 'white',
											}}
										/>
									)}
									onClick={handleQcPageRoute}
									size='small'
									label='Qc page'
								/>
							</>
						)}

					</div>
				)}
			</p>
			<div className={styles['image-grid']}>
				{refreshLoading?.labelId === product?.id && isInternal && (
					<div className={styles.refreshLoading}>
						<CircularProgress />
					</div>
				)}
				{annotations.map((annotation, index) => {
					const cvatStatus = getCvatStatus(annotation);
					const cvatConfig = cvatStatusConfig[cvatStatus];
					const CvatStatusIcon = cvatConfig?.Icon;
					return (
						<div
							key={annotation?.qcId ?? annotation?.id ?? index}
							className='relative w-full max-w-[12.5rem]'
						>
							{isInternal && CvatStatusIcon && (
								<div
									title={cvatConfig.label}
									className={`pointer-events-none absolute right-[3px] top-[1.9rem] z-[6] flex h-6 w-6 items-center justify-center rounded-md border shadow-sm ${cvatConfig.className}`}
								>
									<CvatStatusIcon size={15} strokeWidth={2.2} />
								</div>
							)}
							<ImageThumbnail
								checkedItemList={checkedItemList}
								updateChecked={updateChecked}
								annotation={annotation}
								source={annotation.image}
								identificationType={annotation.ai}
								isInternal={isInternal}
								isUS={isUS}
								egaPercentageSelected={egaPercentageSelected}
								data={annotation}
								categoryHeading={isInternal && product?.label}
								machineDetection={
									isInternal && annotation?.machineDetection
								}
								featureCount={annotation.featureCount}
								onClick={async () => {
									const qcData = await fetchQCData(
										annotation.qcId
									);
									openModal({
										source: annotation.image,
										name: qcData?.ProductName,
										product: product.label,
										date: qcData?.QCTime,
										legendData: {
											legendKeyValues: legendWithColors,
											getValueFunction: (key, value) => {
												if (
													egaPercentageSelected &&
													key !== 'confidence'
												) {
													return {
														color: value?.color,
														fullLabel: `${
															value?.label
														} ${
															value?.hasUnit
																? convertWeightUnit(
																	annotation?.[
																		key
																	],
																	isUS
																		? 'oz'
																		: 'gram'
																)
																: annotation?.[key]
														}`,
													};
												}
												if (
													key === 'confidence' &&
													isInternal &&
													annotation
														?.machineDetection?.[0]
														?.confidence
												) {
													return {
														color: value?.color,
														fullLabel: `${value?.label} ${annotation?.machineDetection?.[0]?.confidence}`,
													};
												}
												return {};
											},
										},
										qcData,
										link:
											qcData != null
												? `${urls.EXTENSION}${urls.PAST_QC}/${qcData?.itemId}?id=${qcData?.id}`
												: null,
									});
								}}
							/>
						</div>
					);
				})}
				{product.count && annotations.length < product.count && (
					<button
						// disabled={disabled}
						disabled={disabled}
						type='button'
						className={styles['load-more']}
						onClick={loadMoreImages}
					>
						{loading ? (
							<div>
								<img src={loadMoreLoader} alt='loader' />
								<p>Loading</p>
							</div>
						) : (
							'Load More'
						)}
					</button>
				)}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const { LIST: productsList } = state.form.ALL_PRODUCTS.data;
	return {
		productsList,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		toggleModal: (show, data = {}) => {
			dispatch(
				modalActions.toggleModal(modalTypes.DEFECT_IMAGE, show, data)
			);
		},
		toggleRetagModal: (show, data = {}) => {
			dispatch(modalActions.toggleModal(modalTypes.RETAG, show, data));
		},
	};
};
export default connect(mapStateToProps, mapDispatchToProps)(ProductImageResult);
